import { Button, Icon, Modal, Text } from 'components';
import { useToast } from 'hooks';
import { ESearchShelfType, TSearchShelfCard } from 'types';
import { TEXT_STRING } from '../../constants';
import { useCallback } from 'react';

const str = TEXT_STRING.SEARCH_PAGE;
const commonStr = TEXT_STRING.COMMON;

type ShelfDetailModalProps = {
  card: TSearchShelfCard;
  type: ESearchShelfType;
  onClose: () => void;
};

function ShelfDetailModal({ card, type, onClose }: ShelfDetailModalProps) {
  const { toastSuccess, toastError } = useToast();

  const handleGoToOfficialSite = useCallback(() => {
    window.open(card?.redirectUrl, '_blank', 'noopener,noreferrer');
  }, [card?.redirectUrl]);

  const handleCopyUrl = useCallback(async () => {
    if (!card?.redirectUrl) return;

    try {
      await navigator.clipboard.writeText(card.redirectUrl);
      toastSuccess(str.SHELF_MODAL_COPY_URL_SUCCESS);
    } catch {
      toastError(new Error(TEXT_STRING.COMMON.SOMETHING_WENT_WRONG));
    }
  }, [card?.redirectUrl, toastError, toastSuccess]);

  return (
    <Modal
      open={!!card}
      onClose={onClose}
      className="bg-white mx-16px rounded-2xl overflow-hidden"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label={commonStr.CLOSE}
        className="absolute top-12px right-12px z-20 flex h-36px w-36px items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm ring-1 ring-white/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
      >
        <Icon icon="close" color="white" width={20} height={20} />
      </button>
      <div className="max-h-[80dvh] overflow-y-auto">
        {/* Banner image */}

        {card.bannerImageUrl && (
          <img
            src={card.bannerImageUrl}
            alt=""
            className="w-full aspect-video object-cover"
          />
        )}

        <div className="p-16px">
          {/* Category label */}
          {card.categoryLabel && (
            <Text
              bold
              fontSize={12}
              className="mb-4px"
              textColor="text-primary"
            >
              {card.categoryLabel}
            </Text>
          )}

          {/* Title + status badge on the same row */}
          <div className="flex items-start justify-between gap-8px mb-6px">
            <Text
              bold
              fontSize={24}
              className="min-w-0 break-words"
              textColor="text-neutral-900"
            >
              {card.title}
            </Text>

            {card.statusBadge && (
              <span className="min-w-0 max-w-[60%] inline-flex items-start gap-4px rounded-full bg-success-50 text-success text-xs font-bold px-8px py-4px">
                <span className="mt-[2px] shrink-0 text-[8px]">●</span>
                <span className="min-w-0 break-words">{card.statusBadge}</span>
              </span>
            )}
          </div>

          {/* Description (whitespace-pre-wrap preserves line breaks and indentation operators type) */}
          <Text className="mb-16px" fontSize={14}>
            {card.description}
          </Text>

          {/* Salary table — JOB only */}
          {type === ESearchShelfType.JOB && card.salaryRows.length > 0 && (
            <div className="mb-16px">
              <p className="text-sm font-bold text-neutral-900 mb-8px">
                {str.SHELF_MODAL_SALARY_TABLE_TITLE}
              </p>
              <table className="w-full text-sm border-collapse border border-neutral">
                <tbody>
                  {card.salaryRows.map((row, index) => (
                    <tr key={index}>
                      <td className="border border-neutral py-8px px-10px font-bold text-neutral-700 whitespace-nowrap w-[40%]">
                        {row.label}
                      </td>
                      <td className="border border-neutral py-8px px-10px text-neutral-600">
                        {row.value}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* CTA buttons */}
          <Button
            onClick={handleGoToOfficialSite}
            block
            bold
            className="mb-8px"
            textSize="text-sm"
          >
            {str.SHELF_MODAL_OFFICIAL_SITE}
          </Button>
          <Button
            onClick={handleCopyUrl}
            block
            bold
            bgColor="bg-white"
            textColor="text-neutral-700"
            border="border border-neutral-300"
            textSize="text-sm"
          >
            {str.SHELF_MODAL_COPY_URL}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default ShelfDetailModal;
